"use client";

import type { ComponentProps } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const markerVariants = cva(
  "inline-flex min-w-0 max-w-full items-center gap-2 rounded-md text-muted-foreground",
  {
    variants: {
      variant: {
        default: "text-sm",
        border: "h-8 border border-border bg-background px-2.5 py-1 text-sm shadow-xs",
        separator:
          "w-full justify-center gap-3 py-2 text-xs before:h-px before:w-20 before:bg-border after:h-px after:w-20 after:bg-border",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export type MarkerProps = ComponentProps<"span"> &
  VariantProps<typeof markerVariants>;

export function Marker({ className, variant, ...props }: MarkerProps) {
  return (
    <span
      data-slot="marker"
      data-variant={variant ?? "default"}
      className={cn(markerVariants({ variant }), className)}
      {...props}
    />
  );
}

export type MarkerIconProps = ComponentProps<"span">;

export function MarkerIcon({ className, ...props }: MarkerIconProps) {
  return (
    <span
      data-slot="marker-icon"
      className={cn(
        "flex h-3.5 w-3.5 shrink-0 items-center justify-center text-text-placeholder [&_svg]:h-3.5 [&_svg]:w-3.5",
        className
      )}
      {...props}
    />
  );
}

export type MarkerContentProps = ComponentProps<"span">;

export function MarkerContent({ className, ...props }: MarkerContentProps) {
  return (
    <span
      data-slot="marker-content"
      className={cn("min-w-0 truncate", className)}
      {...props}
    />
  );
}
